import { Injectable, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  CoursePlayerService,
  CourseNavigationItem,
  CourseStructure,
  PlayerMode
} from '../../../../services/course-player.service';
import { QuizService } from '../../../../services/quiz.service';

/** KAN-349: parsed submit-error payload threaded up to the quiz panel. */
export interface QuizSubmitErrorInfo {
  code?: string;
  message?: string;
  details?: any;
}

/** Attempt-lifecycle 409s the quiz panel renders dedicated recovery UI for
 *  (the generic alert is suppressed for these). */
const ATTEMPT_ERROR_CODES = ['ATTEMPT_EXPIRED', 'ATTEMPT_ALREADY_SUBMITTED', 'ATTEMPT_DRAW_FAILED', 'ATTEMPT_LIMIT_REACHED', 'ATTEMPT_SUPERSEDED'];

/**
 * KAN-191 Bug 1 — Normalize the various response shapes the backend returns
 * for quiz fetches. Returns null when no questions array could be extracted
 * (so the caller keeps any cached value untouched).
 */
export function extractQuizQuestions(response: any): any[] | null {
  if (!response) return null;
  const data = response.data ?? response;
  const questions =
    data?.quiz?.questions ??
    data?.questions ??
    (Array.isArray(data) ? data : null);
  return Array.isArray(questions) ? questions : null;
}

/**
 * KAN-224 — Pull the quiz description out of the same live quiz fetch.
 * The course-structure payload doesn't carry quiz descriptions, so the
 * learner quiz panel sources it from here. Returns null when absent
 * (so the caller keeps any cached value untouched).
 */
export function extractQuizDescription(response: any): string | null {
  if (!response) return null;
  const data = response.data ?? response;
  const description = data?.quiz?.description ?? data?.description;
  return typeof description === 'string' ? description : null;
}

@Injectable()
export class LearnerQuizService {
  private readonly DEBUG_MODE = false;

  constructor(
    private coursePlayerService: CoursePlayerService,
    private quizService: QuizService
  ) {}

  /**
   * KAN-191 Bug 1 — Refetch live quiz data when the learner soft-navigates
   * into a quiz item. The course structure tree is built once at course load
   * and cached, so without a refetch instructor edits never reach the
   * learner without a hard refresh.
   *
   * Mutates `item.quiz` in place after the network resolves. The cached
   * value is shown synchronously to avoid a UI flash; on error we keep the
   * cached value as a fallback so the player keeps working offline.
   *
   * `getCurrentItem` lets the caller race-check that the user hasn't
   * navigated away before a slow response lands (otherwise we'd overwrite
   * the wrong item).
   */
  refreshLiveQuizData(
    item: CourseNavigationItem,
    getCurrentItem: () => CourseNavigationItem | null,
    cdr: ChangeDetectorRef,
    subscriptions: Subscription[]
  ): void {
    const isQuizItem =
      (item.type === 'module' && !!item.quizId) ||
      (item.type === 'unit_quiz' && !!item.quizId);
    if (!isQuizItem) return;

    const fetch$ =
      item.type === 'unit_quiz'
        ? this.quizService.getQuizById(item.quizId!)
        : this.quizService.getQuizByModuleId(item.id);

    const sub = fetch$.subscribe({
      next: (response: any) => {
        const questions = extractQuizQuestions(response);
        const description = extractQuizDescription(response);
        const current = getCurrentItem();
        if (current && current.id === item.id) {
          let changed = false;
          if (questions) {
            current.quiz = questions;
            changed = true;
          }
          // KAN-224: surface the quiz description to the learner panel.
          if (description !== null) {
            current.quizDescription = description;
            changed = true;
          }
          if (changed) cdr.detectChanges();
        }
      },
      error: (err) => {
        // Keep cached value as fallback. Do not blow up the UI.
        console.warn('[LearnerModal] Live quiz refetch failed, using cached value', err);
      }
    });
    subscriptions.push(sub);
  }

  handleQuizSubmit(
    event: { answers: any[]; score: number; passed: boolean; videoProgress?: { furthestPosition: number; duration: number }; attemptId?: string },
    currentItem: CourseNavigationItem,
    courseId: string,
    playerMode: PlayerMode,
    courseProgressMap: Map<string, any>,
    tempInstructorProgress: Map<string, any>,
    courseStructure: CourseStructure | null,
    cdr: ChangeDetectorRef,
    timelineComponent: any,
    reloadProgress: () => void,
    onError?: (info?: QuizSubmitErrorInfo) => void
  ): void {
    const rawId = currentItem.id;
    const isUnitQuiz = rawId.endsWith('_quiz') || currentItem.type === 'unit_quiz';
    const moduleId = isUnitQuiz ? undefined : rawId;
    const unitId = isUnitQuiz ? rawId.replace('_quiz', '') : undefined;

    console.log('[LearnerModal] Quiz submitted:', {
      rawNavigationId: rawId,
      moduleId,
      unitId,
      isUnitQuiz,
      courseId,
      score: event.score,
      passed: event.passed,
      playerMode
    });

    if (playerMode === 'instructor_preview' || playerMode === 'instructor_test') {
      this.handleInstructorQuizSubmit(
        event, currentItem, rawId, moduleId, unitId,
        courseProgressMap, tempInstructorProgress,
        courseStructure, cdr, timelineComponent, reloadProgress
      );
      return;
    }

    this.handleLearnerQuizSubmit(
      event, currentItem, rawId, moduleId, unitId, courseId,
      courseProgressMap, courseStructure, cdr, timelineComponent, reloadProgress, onError
    );
  }

  private handleInstructorQuizSubmit(
    event: { answers: any[]; score: number; passed: boolean },
    currentItem: CourseNavigationItem,
    rawId: string,
    moduleId: string | undefined,
    unitId: string | undefined,
    courseProgressMap: Map<string, any>,
    tempInstructorProgress: Map<string, any>,
    courseStructure: CourseStructure | null,
    cdr: ChangeDetectorRef,
    timelineComponent: any,
    reloadProgress: () => void
  ): void {
    console.log('[LearnerModal] Instructor mode - saving to temporary progress');
    const progressKey = this.coursePlayerService.getProgressKey(currentItem);

    tempInstructorProgress.set(progressKey, {
      moduleId,
      unitId,
      quizCompleted: event.passed,
      // KAN-239: persist the AUTHORITATIVE pass/fail boolean computed by the
      // quiz panel (isQuizPassing on the raw 1dp percentage). The preview emit
      // carries no raw points, so the summary/player display MUST rely on this
      // boolean rather than recomputing from the integer-rounded quizScore.
      quizPassed: event.passed,
      quizScore: event.score,
      moduleCompleted: event.passed,
      videoCompleted: true,
      videoWatchPercentage: 100
    });

    if (event.passed) {
      this.updateNavigationItemStatus(rawId, 'completed', courseStructure, cdr, timelineComponent);
    }

    currentItem.hasSubmission = true;

    courseProgressMap.set(progressKey, {
      ...courseProgressMap.get(progressKey),
      quizCompleted: event.passed,
      // KAN-239: authoritative pass/fail boolean (see tempInstructorProgress above).
      quizPassed: event.passed,
      quizScore: event.score,
      moduleCompleted: event.passed
    });

    reloadProgress();
  }

  private handleLearnerQuizSubmit(
    event: { answers: any[]; score: number | null; passed: boolean | null; videoProgress?: { furthestPosition: number; duration: number }; attemptId?: string },
    currentItem: CourseNavigationItem,
    rawId: string,
    moduleId: string | undefined,
    unitId: string | undefined,
    courseId: string,
    courseProgressMap: Map<string, any>,
    courseStructure: CourseStructure | null,
    cdr: ChangeDetectorRef,
    timelineComponent: any,
    reloadProgress: () => void,
    onError?: (info?: QuizSubmitErrorInfo) => void
  ): void {
    const quizId = currentItem.quizId || null;
    if (!quizId) {
      console.error('[LearnerModal] No quiz ID found for module:', currentItem.id);
      return;
    }

    // KAN-348: submit against the open attempt so grading uses the frozen
    // snapshot (bank draws / shuffled order). videoProgress rides along for
    // the KAN-325 completion-gate race fix.
    this.quizService.submitQuiz(quizId, event.answers, {
      attemptId: event.attemptId,
      moduleId,
      unitId,
      courseId,
      timeSpent: 0,
      videoProgress: event.videoProgress
    }).subscribe({
      next: (result) => {
        console.log('[LearnerModal] Quiz submitted successfully:', result);

        if (result?.data?.result?.passed) {
          this.updateNavigationItemStatus(rawId, 'completed', courseStructure, cdr, timelineComponent);

          // Set unitQuizCompletedAt so populateUnitQuizCompletion works on progress reload
          if (currentItem.type === 'unit_quiz') {
            currentItem.unitQuizCompletedAt = new Date().toISOString();
          }
        }

        currentItem.hasSubmission = true;

        const progressKey = this.coursePlayerService.getProgressKey(currentItem);
        const backendResult = result?.data?.result || {};
        courseProgressMap.set(progressKey, {
          ...courseProgressMap.get(progressKey),
          quizCompleted: backendResult.passed ?? false,
          // KAN-239: store the backend's authoritative pass/fail so all display
          // paths use one source of truth (keeps learner + preview consistent).
          quizPassed: backendResult.passed ?? false,
          quizScore: backendResult.percentageScore ?? 0,
          // KAN-127 follow-up: thread the canonical 1-dp `percentage` (Phase 6
          // backend, the SAME value used for the pass/fail decision) into the
          // progress object that becomes `moduleProgress`. Without this the
          // quiz-panel banner only had the integer `quizScore` (e.g. 80) and its
          // anti-contradiction clamp was a no-op, rendering "(80%)" next to a
          // Failed badge for a 79.5% score. Null-safe: absent on old payloads.
          percentage: backendResult.percentage ?? null,
          quizEarnedPoints: backendResult.earnedPoints,
          quizTotalPoints: backendResult.totalPoints,
          questionResults: backendResult.questionResults,
          // KAN-219 (reopen #5): pass the backend's authoritative feedbackVisibility
          // through AS-IS. Previously this defaulted to 'indicators_answers' when the
          // field was absent, which RELAXED a score_only quiz and leaked per-question
          // indicators on the server-graded "Take Course" path. The submit + course
          // -progress responses now carry the real level; when (defensively) absent we
          // leave it undefined so the quiz-panel keeps the configured @Input level.
          feedbackVisibility: backendResult.feedbackVisibility,
          // KAN-343: pass the backend's authoritative feedback FLAGS through as-is
          // (the quiz-panel prefers these; feedbackVisibility is the legacy fallback).
          feedbackFlags: backendResult.feedbackFlags,
          wrongAnswerFeedback: backendResult.wrongAnswerFeedback || null,
          // KAN-327: thread the gated custom pass message into moduleProgress so
          // the quiz-panel can render the success banner.
          passFeedback: backendResult.passFeedback || null,
          gradingStatus: backendResult.gradingStatus,
          manualGradingPending: backendResult.manualGradingPending,
          manualPendingPoints: backendResult.manualPendingPoints,
          bestPossiblePercentage: backendResult.bestPossiblePercentage,
          guaranteedFail: backendResult.guaranteedFail,
          // KAN-314 VS1: thread attempt accounting into moduleProgress so the
          // quiz-panel can withhold "Try Again" once the cap is reached.
          attemptLimit: backendResult.attemptLimit ?? null,
          attemptsUsed: backendResult.attemptsUsed ?? null,
          attemptsRemaining: backendResult.attemptsRemaining ?? null,
          moduleCompleted: result.moduleCompleted || false
        });

        cdr.detectChanges();
        reloadProgress();
      },
      error: (error) => {
        console.error('[LearnerModal] Quiz submission failed:', error);
        // Backend error envelope is { success: false, error: { message, code, details } } — try
        // the wrapped path first, then fall back to a flat shape, then to a generic message.
        const errorMessage =
          error?.error?.error?.message ||
          error?.error?.message ||
          'Failed to submit quiz. Please try again.';
        const code = error?.error?.error?.code || error?.error?.code;
        const details = error?.error?.error?.details ?? null;

        // KAN-349: attempt-lifecycle 409s get dedicated recovery UI in the quiz
        // panel — suppress the generic alert for those; keep it for everything else.
        if (!ATTEMPT_ERROR_CODES.includes(code)) {
          alert(errorMessage);
        }
        if (onError) {
          onError({ code, message: errorMessage, details });
        }
      }
    });
  }

  updateNavigationItemStatus(
    moduleId: string,
    status: 'completed' | 'current' | 'available',
    courseStructure: CourseStructure | null,
    cdr: ChangeDetectorRef,
    timelineComponent: any
  ): void {
    if (!courseStructure) return;

    const item = courseStructure.navigationItems.find(i => i.id === moduleId);
    if (item) {
      item.status = status;
      console.log('[LearnerModal] Updated navigation item status:', { moduleId, status });
      cdr.detectChanges();

      if (timelineComponent) {
        timelineComponent.calculateProgress();
      }
    }
  }
}
